import { useParams } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../ui/pagination';

interface TxHistoryTablePaginationProps {
  currentPage: number;
  totalPages: number;
  currentSort: 'newest' | 'oldest';
  tableRef?: React.RefObject<HTMLDivElement>;
}

export const TxHistoryTablePagination = ({
  currentPage,
  totalPages,
  currentSort,
  tableRef,
}: TxHistoryTablePaginationProps) => {
  const { id: address } = useParams<{ id: string }>();

  if (totalPages <= 1) return null;

  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('sort', currentSort);
    return `/bitcoin/address/${address}?${params}`;
  };

  const handlePageClick = () => {
    // Optimistic scroll - scroll immediately when user clicks
    if (tableRef?.current) {
      const element = tableRef.current;
      const elementTop = element.offsetTop;
      const offset = -300;

      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth',
      });
    }
  };

  const getVisiblePages = () => {
    const delta = 2; // How many pages to show before and after current page
    const range = [];
    const rangeWithDots = [];

    // Calculate range around current page
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Always add page 1
    rangeWithDots.push(1);

    // Add dots and remaining pages
    if (range[0] > 2) {
      rangeWithDots.push('dots1');
    }

    range.forEach((num) => rangeWithDots.push(num));

    if (range[range.length - 1] < totalPages - 1) {
      rangeWithDots.push('dots2');
    }

    // Always add last page if there are more pages
    if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="mt-6">
      <Pagination>
        <PaginationContent>
          {currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious
                to={buildPageUrl(currentPage - 1)}
                onClick={handlePageClick}
                preventScrollReset={true}
              />
            </PaginationItem>
          )}

          {getVisiblePages().map((pageNumber, index) => {
            if (pageNumber === 'dots1' || pageNumber === 'dots2') {
              return (
                <PaginationItem key={`${pageNumber}-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  to={buildPageUrl(pageNumber as number)}
                  isActive={pageNumber === currentPage}
                  onClick={handlePageClick}
                  preventScrollReset={true}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {currentPage < totalPages && (
            <PaginationItem>
              <PaginationNext
                to={buildPageUrl(currentPage + 1)}
                onClick={handlePageClick}
                preventScrollReset={true}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};
